import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HeartIcon, BrainIcon, SparklesIcon, SunIcon, Users, ArrowRightIcon, MenuIcon, XIcon, MoonIcon } from 'lucide-react';
import { ContactSection } from '../components/ContactSection';
import { CourseCalendar } from '../components/CourseCalendar';
import { HeroRotator } from '../components/HeroRotator';
import { Logo } from '../components/Logo';
import { currentCourse } from '../data/courses';
// ═══════════════════════════════════════════════════════════
// 🔗 REGISTRACIJOS NUORODA — čia įrašykite savo anketos URL
// ═══════════════════════════════════════════════════════════
const REGISTRATION_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSdN1pDWRqjo_eykjFE7VECqifWUKuHBRr2Fq-LVvCOnP6_lwg/viewform?usp=publish-editor';
export function LandingPage({
  mode,
  toggleMode,
  onViewCourses
}: {
  mode: 'light' | 'dark';
  toggleMode: () => void;
  onViewCourses: () => void;
}) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [testimonialDirection, setTestimonialDirection] = useState(0);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const fadeInUp = {
    initial: {
      opacity: 0,
      y: 30
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-80px'
    },
    transition: {
      duration: 0.65,
      ease: 'easeOut'
    }
  };
  const staggerContainer = {
    initial: {},
    whileInView: {
      transition: {
        staggerChildren: 0.1
      }
    },
    viewport: {
      once: true,
      margin: '-80px'
    }
  };
  // Reusable section label
  const SectionLabel = ({
    text,
    light = false
  }: {
    text: string;
    light?: boolean;
  }) => <div className="flex flex-col items-center mb-5" data-id="element-130" data-mp="306zewtq03wc0">
      <p className={`text-xs uppercase tracking-widest font-semibold mb-3 ${light ? 'text-gold-light/70' : 'text-gold-dark'}`} data-id="element-131" data-mp="308asfl990igb">
        {text}
      </p>
      <div className="w-10 h-0.5 bg-gold" data-id="element-132" data-mp="3975zt0o13yl7" />
    </div>;
  return <main className="w-full bg-sand-lightest transition-colors duration-300" data-id="element-133" data-mp="39a0jur34rhe3">
      {/* ── STICKY NAV ── */}
      <motion.nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-500" style={{
      background: 'rgba(248, 243, 238, 0.92)',
      backdropFilter: 'blur(12px)',
      boxShadow: scrolled ? '0 1px 0 rgba(27, 27, 75, 0.10)' : 'none'
    }} initial={{
      y: -80,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      duration: 0.6,
      ease: 'easeOut',
      delay: 2.0
    }} data-id="element-134" data-mp="3j5v75ziau0n7">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between" data-id="element-135" data-mp="pe25mjl11yjh">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2 select-none" data-id="element-136" data-mp="ubbrjp97js2k">
            <Logo className="h-9 md:h-10" data-id="element-137" data-mp="ffco4rx1mg3r" />
          </a>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8" data-id="element-138" data-mp="kd4kpz8muqea">
            {[{
            label: 'Apie',
            href: '#apie'
          }, {
            label: 'Metodika',
            href: '#metodika'
          }, {
            label: 'Programa',
            href: '#programa'
          }, {
            label: 'Komanda',
            href: '#komanda'
          }, {
            label: 'Tvarkaraštis',
            href: '#tvarkarastis'
          }].map(link => <a key={link.label} href={link.href} className="text-sm font-medium uppercase tracking-wider transition-colors duration-200 text-[#1B1B4B]/70 hover:text-[#1B1B4B]" data-id="element-139" data-mp="3nt32xpgv0a09">
                {link.label}
              </a>)}
          </div>

          {/* CTA & Dark Mode */}
          <div className="hidden md:flex items-center gap-4" data-id="element-140" data-mp="322a2r6o98knd">
            <button onClick={toggleMode} className="p-2 rounded-full transition-colors text-[#1B1B4B]/80 hover:text-[#1B1B4B] hover:bg-[#1B1B4B]/10" aria-label="Toggle dark mode" data-id="element-141" data-mp="3220b7enka5fk">
              {mode === 'light' ? <MoonIcon className="w-5 h-5" data-id="element-142" data-mp="2o3k59pxiwiyh" /> : <SunIcon className="w-5 h-5" data-id="element-143" data-mp="2j3o2fu0kaxki" />}
            </button>
            <motion.a href="https://www.instagram.com/vestirna/" target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.04
          }} whileTap={{
            scale: 0.97
          }} className="inline-flex items-center gap-2 text-white px-6 py-2.5 rounded-full text-sm font-semibold transition-colors shadow-lg" style={{
            background: 'linear-gradient(135deg, #F72585 0%, #7209B7 100%)'
          }} data-id="element-144" data-mp="2j5sc6sl56qsp">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" data-id="element-145" data-mp="13josc9xjfdzz">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" data-id="element-146" data-mp="6virzz7nf7ri" />
              </svg>
              Parašyk mums
            </motion.a>
          </div>

          {/* Mobile burger & Dark Mode */}
          <div className="flex items-center gap-2 md:hidden" data-id="element-147" data-mp="ns5wvyzwikn6">
            <button onClick={toggleMode} className="p-2 rounded-full transition-colors text-[#1B1B4B]/80 hover:text-[#1B1B4B] hover:bg-[#1B1B4B]/10" aria-label="Toggle dark mode" data-id="element-148" data-mp="iwqjt99pwsrt">
              {mode === 'light' ? <MoonIcon className="w-5 h-5" data-id="element-149" data-mp="53hi7us0hbju" /> : <SunIcon className="w-5 h-5" data-id="element-150" data-mp="51oa0b1r2j9q" />}
            </button>
            <button className="p-1 transition-colors text-[#1B1B4B]/80 hover:text-[#1B1B4B]" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Meniu" data-id="element-151" data-mp="2tqt7zvesp3">
              {mobileOpen ? <XIcon className="w-6 h-6" data-id="element-152" data-mp="21ulqfp9w5u3c" /> : <MenuIcon className="w-6 h-6" data-id="element-153" data-mp="21s90aotzcdf7" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence data-id="element-154" data-mp="2uihlkpjtbdy0">
          {mobileOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.3
        }} className="md:hidden overflow-hidden" style={{
          background: '#1e0664'
        }} data-id="element-155" data-mp="2ugy5p022j4wr">
              <div className="px-6 py-4 flex flex-col gap-4 border-t border-white/10" data-id="element-156" data-mp="25scdcso6hiey">
                {[{
              label: 'Apie',
              href: '#apie'
            }, {
              label: 'Metodika',
              href: '#metodika'
            }, {
              label: 'Programa',
              href: '#programa'
            }, {
              label: 'Komanda',
              href: '#komanda'
            }, {
              label: 'Tvarkaraštis',
              href: '#tvarkarastis'
            }].map(link => <a key={link.label} href={link.href} onClick={() => setMobileOpen(false)} className="text-white/80 hover:text-white text-base font-medium uppercase tracking-wider py-1 transition-colors" data-id="element-157" data-mp="2aqnl8xuwxiuj">
                    {link.label}
                  </a>)}
                <a href="https://www.instagram.com/vestirna/" target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)} className="inline-flex items-center justify-center gap-2 text-white px-6 py-3 rounded-full text-sm font-semibold text-center mt-2" style={{
              background: 'linear-gradient(135deg, #F72585 0%, #7209B7 100%)'
            }} data-id="element-158" data-mp="3fr51j0wx7dpl">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" data-id="element-159" data-mp="7cd9bs8m8l87">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" data-id="element-160" data-mp="gc03bsz0mor8" />
                  </svg>
                  Parašyk mums
                </a>
              </div>
            </motion.div>}
        </AnimatePresence>
      </motion.nav>

      {/* ── HERO (rotuojamas) ── */}
      <HeroRotator data-id="element-161" data-mp="jhhcotuy2fg5" />

      {/* ── INTRO ── */}
      <section id="apie" className="py-20 px-4 bg-sand-light" data-id="element-162" data-mp="2wqz4b2fubkvp">
        <motion.div className="max-w-3xl mx-auto text-center" {...fadeInUp} data-id="element-163" data-mp="2wtbshxc6yrh7">
          <p className="text-lg md:text-xl text-mountain-dark leading-relaxed font-[300]" data-id="element-164" data-mp="2ryxtzm28g48b">
            Ne visada nuo tavęs priklauso, kas nutiks tavo gyvenime, tačiau gali sąmoningai spręsti, kaip reaguosi į tai, kas vyksta. Šis kursas padės geriau pažinti savo reakcijas, valdyti dėmesį ir sąmoningai pasirinkti būseną, kurioje nori būti.

          </p>
        </motion.div>
      </section>

      {/* ── CHALLENGES ── */}
      <section className="py-24 px-4 bg-sand-lightest" data-id="element-165" data-mp="3bmdkvanl6cx2">
        <div className="max-w-6xl mx-auto" data-id="element-166" data-mp="36oc4wylgitpt">
          <motion.div className="mb-12" {...fadeInUp} data-id="element-167" data-mp="36rhqw4yshfhb">
            <p className="font-accent italic text-gold-dark mb-4 text-xl" data-id="element-168" data-mp="31p8zhd1kelhb">
              Visiems Pažįstami
            </p>
            <div className="h-[2px] w-[160px] mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-169" data-mp="1vbt35bdpocfg" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-170" data-mp="1wrx74lwg9mge">
              Kasdieniai Iššūkiai
            </h2>
          </motion.div>

          <motion.div className="grid grid-cols-2 md:grid-cols-4 gap-4" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-171" data-mp="1itqizxhaz0tx">
            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-172" data-mp="1dumaza647956">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1537210249814-b9a10a161ae4?w=600&q=80')"
            }} data-id="element-173" data-mp="140dhok99fc5y" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-174" data-mp="bgi4h2955qh7" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-175" data-mp="geb7xnj286ds">
                01
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-176" data-mp="144dzm2j2babv">
                <p className="text-lg font-bold text-white mb-1" data-id="element-177" data-mp="z5kk64k7t992">
                  Nuolatinis stresas
                </p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-178" data-mp="1d6exn1m4e0el">
                  Kaip efektyviai spręsti problemas neprarandant vidinės
                  ramybės?
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-179" data-mp="32kwmclw3mzxp">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://cdn.magicpatterns.com/uploads/fkDNhwZwL2Fman3fp7TuCW/moritz-kindler-n6XW1P6a-_U-unsplash.jpg')"
            }} data-id="element-180" data-mp="3cewpii7tlv3s" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-181" data-mp="2tm2l2i7tpldt" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-182" data-mp="8xwkxrd4o2ks">
                02
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-183" data-mp="1tg8irk3712j3">
                <p className="text-lg font-bold text-white mb-1" data-id="element-184" data-mp="1oll3mbu2xkh6">
                  Dėmesio blaškymasis
                </p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-185" data-mp="1jm8grr37kdel">
                  Kaip valdyti dėmesį nuolat besikeičiančioje aplinkoje?
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-186" data-mp="19s80qu6e1nr3">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1669369172813-938c5aaa8685?w=600&q=80')"
            }} data-id="element-187" data-mp="302aywzwysrw0" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-188" data-mp="3e5qkvh7wwbsu" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-189" data-mp="3a7155hzeii61">
                03
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-190" data-mp="3nxefts3ogaz2">
                <p className="text-lg font-bold text-white mb-1" data-id="element-191" data-mp="3svocy88yplnb">Perdegimas</p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-192" data-mp="2chy46o6we97c">
                  Kaip siekti ilgalaikių tikslų neperdegant?
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-193" data-mp="29xjowt1t7lhp">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1516490981167-dc990a242afe?w=600&q=80')"
            }} data-id="element-194" data-mp="2iz9c1monry9o" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-195" data-mp="1m8qp2li2gtht" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-196" data-mp="1v72ckenmzb61">
                04
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-197" data-mp="i10r8s2zca3u">
                <p className="text-lg font-bold text-white mb-1" data-id="element-198" data-mp="m0rlx6vmnadn">Chaosas</p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-199" data-mp="qyb0y905fr6n">
                  Ką daryti su emocijomis sudėtingose situacijose?
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── TARP NUOLATINĖS KAITOS ── */}
      <section className="py-24 px-4 bg-mountain-darker text-white" data-id="element-200" data-mp="14yc4xxbpneqc">
        <div className="max-w-6xl mx-auto" data-id="element-201" data-mp="19pu6onlk5jcd">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-202" data-mp="19r54m6sbnwvk">
            <p className="font-accent italic text-gold-light mb-4 text-xl" data-id="element-203" data-mp="1epgeuimj49eb">
              Paieškos
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
          }} data-id="element-204" data-mp="1jp2tmwi09xp8" />
            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight" data-id="element-205" data-mp="34629srhvqk0v">
              Tarp nuolatinės kaitos ir pusiausvyros paieškų
            </h2>
          </motion.div>

          <motion.div className="flex flex-col gap-12 items-center" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-206" data-mp="2z8sljf688eem">
            <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto" data-id="element-207" data-mp="3mywemslsgvrd">
              <p className="text-[20px] text-white/80 font-[300]" data-id="element-208" data-mp="3d7a5r2wxb8rs">
                Šiandienos pasaulis sukasi neregėtu tempu. Informacijos srautas,
                nuolatiniai reikalavimai ir pokyčiai kuria vidinę įtampą, kuri
                laikui bėgant virsta stresu, perdegimo jausmu ir prarastu ryšiu
                su savimi.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="w-full max-w-2xl mx-auto space-y-0" data-id="element-209" data-mp="1vbrde8km4nae">
              {['Kaip efektyviai spręsti problemas neprarandant vidinės ramybės?', 'Kaip giliai atsipalaiduoti ir tikrai pailsėti?', 'Kaip pasiekti gyvenime harmoniją ir pilnatvę?', 'Kaip valdyti emocijas ir dėmesį sudėtingose situacijose?'].map((question, i) => <div key={i} className="flex items-start gap-5 py-6" style={{
              borderBottom: i < 3 ? '1px solid rgba(255,255,255,0.1)' : 'none'
            }} data-id="element-210" data-mp="2f1v2ywem6duv" data-mpi={i}>
                  <span className="font-bold text-gold-light flex-shrink-0" style={{
                fontSize: '2rem',
                lineHeight: 1.1
              }} data-id="element-211" data-mp="32qw2tv9dsgoj">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <p className="text-white/80 text-lg leading-relaxed pt-0.5" data-id="element-212" data-mp="3aaox9e2u3jvu">
                    {question}
                  </p>
                </div>)}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── WHAT IS DFB ── */}
      <section id="metodika" className="relative overflow-hidden" style={{
      minHeight: 600
    }} data-id="element-213" data-mp="3o83p0pgk83qz">
        {/* Full-width background image */}
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1920&q=80')"
      }} data-id="element-214" data-mp="1ud9z8o7sbsm" />
        {/* Left-to-right cinematic gradient */}
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(to right, rgba(0,0,0,0.90) 0%, rgba(0,0,0,0.72) 40%, rgba(0,0,0,0.25) 70%, transparent 100%)'
      }} data-id="element-215" data-mp="ftv483ka4h9d" />

        {/* Content — left half */}
        <motion.div className="relative z-10 flex items-center" style={{
        minHeight: 600
      }} {...fadeInUp} data-id="element-216" data-mp="2lmay4mzrt3yb">
          <div className="max-w-7xl mx-auto w-full px-8 md:px-16 py-24" data-id="element-217" data-mp="2gt882ou7ccmv">
            <div className="max-w-xl" data-id="element-218" data-mp="2vi4bg8lbd1h5">
              {/* Italic subtitle — now above */}
              <p className="font-accent italic text-gold-light mb-4 text-xl" data-id="element-219" data-mp="2qkchza50t7au">
                Sprendimas
              </p>
              {/* Gold line */}
              <div className="h-[2px] w-[160px] mb-4 rounded-full" style={{
              background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
            }} data-id="element-220" data-mp="2zl0ycbhvzjqw" />
              {/* Section heading */}
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-10 leading-tight" data-id="element-221" data-mp="3gulbnra2trmu">
                Sąmoningas Būsenų Valdymas
              </h2>
              {/* Paragraphs */}
              <p className="text-white/80 mb-5 font-[300] text-[20px]" data-id="element-222" data-mp="3832xflwoxlwe">
                Diferencijuotos Funkcinės Būsenos – tai aktyvios
                savireguliacijos metodika, leidžianti sąmoningai valdyti vidines
                būsenas nepriklausomai nuo išorinės veiklos.
              </p>
              <p className="text-white/80 font-[300] text-[20px]" data-id="element-223" data-mp="3336pjbkvhabk">
                Metodika unikali tuo, kad moko ne slopinti emocijas, o jas
                diferencijuoti, sąmoningai pasirinkti ir pritaikyti.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── 16 STATES ── (dark bg) */}
      <section className="py-24 px-4 bg-mountain-darker text-white" data-id="element-224" data-mp="ndnaj20lehc1">
        <div className="max-w-6xl mx-auto" data-id="element-225" data-mp="refk5wddmo8u">
          <motion.div className="text-center mb-10" {...fadeInUp} data-id="element-226" data-mp="rd4hjzvo3mjb">
            <p className="font-accent italic text-gold-light mb-4 text-xl" data-id="element-227" data-mp="2m89woiivqi3">
              Aktyvios Savireguliacijos Sistema
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
          }} data-id="element-228" data-mp="3trhsvw83n4i7" />
            <h3 className="text-3xl md:text-4xl font-bold text-white" data-id="element-229" data-mp="3ful4s2jd8150">
              16 Funkcinių Būsenų
            </h3>
          </motion.div>

          <motion.div className="mb-16 max-w-3xl mx-auto" {...fadeInUp} data-id="element-230" data-mp="3b18g5seoruc9">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-8 rounded-2xl text-center" data-id="element-231" data-mp="3649orhmeq1hr">
              <p className="text-white/80 leading-relaxed" data-id="element-232" data-mp="312sptrnhgp3t">
                DFB metodika dalinasi į žvalgą: kiekvienai veiklai reikalinga
                skirtinga psichikos būsena. Trumpų distancijų bėgimas reikalauja
                kitokio pasirengimo nei maratonas. Lengvas pokalbis – tai viena
                būsena, o intensyvus protinis darbas – visai kita.
              </p>
            </div>
          </motion.div>

          {/* Vertical Timeline Flow */}
          <motion.div className="relative max-w-4xl mx-auto py-8" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-233" data-mp="1gnw731r3cy6x">
            {/* Central Vertical Line */}
            <div className="absolute left-8 md:left-1/2 top-12 bottom-12 w-0.5 bg-gradient-to-b from-gold/60 via-gold/40 to-gold/20 -translate-x-1/2" data-id="element-234" data-mp="sv7fn9afwj16" />

            <div className="flex flex-col gap-16 md:gap-20 relative z-10" data-id="element-235" data-mp="12o50hm2wmk2y">
              {[{
              title: '4 Mentalinio Aktyvumo Būsenos',
              desc: 'Nuo chaoso iki tikslaus mąstymo',
              img: 'https://cdn.magicpatterns.com/uploads/86D17L5NgjqReE3GCNF7d3/Screenshot_2026-03-06_at_13.44.55.png',
              imgStyle: {
                backgroundSize: '250%',
                backgroundPosition: '55% 35%'
              } as React.CSSProperties
            }, {
              title: '4 Emocinio Aktyvumo Būsenos',
              desc: 'Nuo gilumo ir jautrumo iki lengvumo ir dinamiškumo bendraujant',
              img: 'https://cdn.magicpatterns.com/uploads/oYRvzX8HGPdzN3RmPEhdeg/zac-durant-_6HzPU9Hyfg-unsplash.jpg',
              imgStyle: {} as React.CSSProperties
            }, {
              title: '4 Fizinio Aktyvumo Būsenos',
              desc: 'Nuo plačių, erdvinių judesių iki tikslingo, koordinuotos veiklos',
              img: 'https://cdn.magicpatterns.com/uploads/6RbKwQ7msGyRimZEELWpys/atharva-tulsi-2jA_YoQuvaA-unsplash.jpg',
              imgStyle: {} as React.CSSProperties
            }, {
              title: '4 Harmoningo Aktyvumo Būsenos',
              desc: 'Kūnas, emocijos ir protas veikia darniai kaip viena visuma',
              img: 'https://cdn.magicpatterns.com/uploads/deWv2jrD7JtPFbuwEysfBb/IMG_7871.jpg',
              imgStyle: {
                backgroundSize: '180%',
                backgroundPosition: '50% 40%'
              } as React.CSSProperties
            }].map((item, idx) => {
              const isEven = idx % 2 === 0;
              return <motion.div key={idx} variants={fadeInUp} className={`relative flex flex-col md:flex-row items-start md:items-center w-full ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`} data-id="element-236" data-mp="357wkhnk1e36q" data-mpi={idx}>
                    {/* Mobile Dot */}
                    <div className="md:hidden absolute left-8 top-12 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-gold ring-4 ring-mountain-darker z-20" data-id="element-237" data-mp="3k3vrsvzbnzat" />

                    {/* Desktop Center Dot */}
                    <div className="hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-gold ring-4 ring-mountain-darker z-20" data-id="element-238" data-mp="1k3pegb81oo5k" />

                    {/* Image Side */}
                    <div className={`w-full md:w-1/2 flex pl-20 md:pl-0 ${isEven ? 'md:justify-end md:pr-16' : 'md:justify-start md:pl-16'} mb-5 md:mb-0`} data-id="element-239" data-mp="1a61jplafvgne">
                      <div className="relative z-10 w-24 h-24 rounded-full overflow-hidden border-4 border-gold ring-2 ring-gold/25 flex-shrink-0 shadow-xl" data-id="element-240" data-mp="22tp0ep8l9n4r">
                        <div className="w-full h-full bg-cover bg-center" style={{
                      backgroundImage: `url('${item.img}')`,
                      ...item.imgStyle
                    }} data-id="element-241" data-mp="1xx6ydx4n82w4" />
                      </div>
                    </div>

                    {/* Text Side */}
                    <div className={`w-full md:w-1/2 flex flex-col pl-20 md:pl-0 ${isEven ? 'md:items-start md:pl-16' : 'md:items-end md:pr-16 md:text-right'}`} data-id="element-242" data-mp="2u3jrprun16gx">
                      <h4 className="font-bold text-white mb-2 text-[20px]" data-id="element-243" data-mp="330ak9u9ze36r">
                        {item.title}
                      </h4>
                      <p className="text-sm text-white/60 leading-relaxed max-w-sm" data-id="element-244" data-mp="3csq4ky8r33ut">
                        {item.desc}
                      </p>
                    </div>
                  </motion.div>;
            })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── BENEFITS ── (light bg) */}
      <section className="py-24 px-4 bg-sand-lightest" data-id="element-245" data-mp="3qrg2uh7b39kr">
        <div className="max-w-5xl mx-auto" data-id="element-246" data-mp="1rfdhf1f4g54g">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-247" data-mp="1rgonx8zybqz9">
            <p className="font-accent italic text-gold-dark mb-4 text-xl" data-id="element-248" data-mp="1wezwlgeifvfw">
              Rezultatai
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-249" data-mp="1hyyprlbjsfzf" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-250" data-mp="1d047qllemvgd">
              DFB Metodologijos Nauda
            </h2>
          </motion.div>

          <motion.div variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-251" data-mp="20o57p64ip3k6">
            {/* First row: 3 items */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-12 mb-12" data-id="element-252" data-mp="1vpk6p4hfyfuo">
              {[{
              icon: <HeartIcon className="w-12 h-12 text-gold-dark" data-id="element-253" data-mp="gchfz0za0199" />,
              title: 'Nuolatinė Ramybė',
              desc: 'Stabilumas nepriklausomai nuo išorinio pasaulio'
            }, {
              icon: <BrainIcon className="w-12 h-12 text-gold-dark" data-id="element-254" data-mp="7tmdmzkq1rqd" />,
              title: 'Dėmesio Valdymas',
              desc: 'Sąmoningas dėmesio nukreipimas į svarbius dalykus'
            }, {
              icon: <SparklesIcon className="w-12 h-12 text-gold-dark" data-id="element-255" data-mp="hsv7lsfl76cq" />,
              title: 'Aktyvi Savireguliacija',
              desc: 'Efektyvus streso ir emocijų valdymas'
            }].map((benefit, i) => <motion.div key={i} variants={fadeInUp} className="flex flex-col items-center text-center" data-id="element-256" data-mp="3l6fsf35nv1nz" data-mpi={i}>
                  {benefit.icon}
                  <div className="w-8 h-px bg-gold mx-auto my-4" data-id="element-257" data-mp="37gm36jq8ufdq" />
                  <h3 className="text-xl font-bold text-mountain-darker mb-2" data-id="element-258" data-mp="37f154k4q0eb4">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-mountain-dark/60 max-w-[180px] mx-auto leading-relaxed" data-id="element-259" data-mp="3s66el6q0erxs">
                    {benefit.desc}
                  </p>
                </motion.div>)}
            </div>

            {/* Second row: 2 items centered */}
            <div className="flex flex-col sm:flex-row justify-center gap-12" data-id="element-260" data-mp="4uxb16timktu">
              {[{
              icon: <Users className="w-12 h-12 text-gold-dark" data-id="element-261" data-mp="397c0lzyddvio" />,
              title: 'Emocinis Intelektas',
              desc: 'Geresnis savęs ir kitų supratimas'
            }, {
              icon: <SunIcon className="w-12 h-12 text-gold-dark" data-id="element-262" data-mp="3i7h9kej7p1yy" />,
              title: 'Giluminis Poilsis',
              desc: 'Efektyvus atsigavimas per trumpą laiką'
            }].map((benefit, i) => <motion.div key={i} variants={fadeInUp} className="flex flex-col items-center text-center sm:w-[calc(33.333%-1.5rem)]" data-id="element-263" data-mp="xgzx6l5cp8dc" data-mpi={i}>
                  {benefit.icon}
                  <div className="w-8 h-px bg-gold mx-auto my-4" data-id="element-264" data-mp="2hzkckj2hi1t6" />
                  <h3 className="text-xl font-bold text-mountain-darker mb-2" data-id="element-265" data-mp="2i0c3vhp1tddn">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-mountain-dark/60 max-w-[180px] mx-auto leading-relaxed" data-id="element-266" data-mp="28a0nuws3bdqe">
                    {benefit.desc}
                  </p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── COURSE OFFER ── (dark bg) */}
      <section id="registracija" className="py-24 px-4 bg-mountain-darker text-white" data-id="element-267" data-mp="1u96eobx7x8qo">
        <div className="max-w-5xl mx-auto" data-id="element-268" data-mp="1pby7t7hisr4q">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-269" data-mp="1pczfr338oxi8">
            <p className="font-accent italic text-gold-light mb-4 text-xl" data-id="element-270" data-mp="3ompmcekyq7pp">
              DFB Metodika: Medituoti Lengva
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
          }} data-id="element-271" data-mp="3jn37k0phkjes" />
            <h2 className="text-4xl md:text-5xl font-bold text-white" data-id="element-272" data-mp="3hqwczmtbbsxo">
              Prisijunk Prie Kurso
            </h2>
          </motion.div>

          <motion.div className="flex flex-col sm:flex-row items-stretch justify-center mb-12" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-273" data-mp="38z44virrgvw9">
            {[{
            value: '90',
            label: 'Akademinių valandų',
            sub: '30 užsiėmimų po 2–2.5 val.'
          }, {
            value: '2.5',
            label: 'Mėnesiai',
            sub: 'Programos trukmė'
          }, {
            value: '10',
            label: 'Dalyvių max',
            sub: 'Optimali grupė'
          }, {
            value: '110€',
            label: 'už mėnesį',
            sub: 'Galimybė mokėti dalimis'
          }].map((stat, i, arr) => <motion.div key={stat.value} variants={fadeInUp} className="flex-1 flex flex-col items-center text-center px-8 py-8 relative" data-id="element-274" data-mp="2fdtk9r4qpav4" data-mpi={i}>
                {i < arr.length - 1 && <div className="hidden sm:block absolute right-0 top-1/2 -translate-y-1/2" style={{
              width: 1,
              height: '60%',
              background: 'rgba(255,255,255,0.12)'
            }} data-id="element-275" data-mp="3358su1n40oet" />}
                <span className="font-bold text-gold-light leading-none mb-3" style={{
              fontSize: 'clamp(2.5rem, 5vw, 3.5rem)'
            }} data-id="element-276" data-mp="3854v9wg5s7s9">
                  {stat.value}
                </span>
                <p className="text-sm font-semibold text-white uppercase tracking-wide mb-1" data-id="element-277" data-mp="2equ3oiqxqqwy">
                  {stat.label}
                </p>
                <p className="text-xs text-white/50 leading-relaxed" data-id="element-278" data-mp="2jj3qvr0o1cvj">
                  {stat.sub}
                </p>
              </motion.div>)}
          </motion.div>

          <div className="w-full h-px mb-12" style={{
          background: 'rgba(255,255,255,0.1)'
        }} data-id="element-279" data-mp="2tef60afzkm3t" />

          <motion.div className="text-center" {...fadeInUp} data-id="element-280" data-mp="32gerbey1acck">
            <motion.a href="https://www.instagram.com/vestirna/" target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.05,
            boxShadow: '0 20px 50px -10px rgba(0,0,0,0.5)'
          }} whileTap={{
            scale: 0.97
          }} className="inline-flex items-center gap-2.5 whitespace-nowrap text-white px-10 py-[15px] rounded-full text-base font-semibold shadow-2xl transition-colors mb-5" style={{
            background: 'linear-gradient(135deg, #F72585 0%, #7209B7 100%)'
          }} data-id="element-281" data-mp="32ekdslp3dmii">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" data-id="element-282" data-mp="1jgnto8w33hby">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" data-id="element-283" data-mp="1eimc5seb3q2l" />
              </svg>
              Parašyk mums žinutę
            </motion.a>
            <div className="flex items-center justify-center gap-2 mt-3" data-id="element-284" data-mp="2gx4zqb3mvh5a">
              <p className="text-sm text-white/50" data-id="element-285" data-mp="2gz7oyswyp68y">
                Įdomu? Turi klausimų? Tiesiog parašyk ✨
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── COURSE STRUCTURE ── (light bg) */}
      <section id="programa" className="py-24 px-4 bg-sand-light" data-id="element-286" data-mp="2vudtfbgesx06">
        <div className="max-w-6xl mx-auto" data-id="element-287" data-mp="30ldt2ogwxt6d">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-288" data-mp="30m5o9y6llow4">
            <p className="font-accent italic text-gold-dark mb-4 text-xl" data-id="element-289" data-mp="39zxmskogry21">
              3 etapai: išmok, patirk, pritaikyk
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-290" data-mp="35y2prsdkg6u2" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-291" data-mp="2r0s5btiy3qm7">
              Kurso Struktūra
            </h2>
          </motion.div>

          <motion.div className="space-y-4" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-292" data-mp="2n1jeoxj7c8uv">
            {[{
            num: '01',
            title: 'Pirmoji Dalis: Pagrindai',
            desc: 'Baziniai pratimai ir teoriniai pagrindai',
            img: 'https://images.unsplash.com/photo-1712342109846-a8fcb1c883ba?w=1200&q=80'
          }, {
            num: '02',
            title: 'Antroji Dalis: Būsenos',
            desc: '16 aktyvumo būsenų atpažinimas ir valdymas',
            img: 'https://images.unsplash.com/photo-1703945530393-600961291ced?w=1200&q=80'
          }, {
            num: '03',
            title: 'Trečioji Dalis: Integracija',
            desc: 'Įgūdžių taikymas kasdieniame gyvenime',
            img: 'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?w=1200&q=80'
          }].map(part => <motion.div key={part.num} variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 200
          }} data-id="element-293" data-mp="1pjlkqs5yazw7">
                {/* Background image */}
                <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: `url('${part.img}')`
            }} data-id="element-294" data-mp="38j2nbw800xk9" />
                {/* Left-to-right dark gradient */}
                <div className="absolute inset-0" style={{
              background: 'linear-gradient(to right, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.45) 55%, rgba(0,0,0,0.1) 100%)'
            }} data-id="element-295" data-mp="2ympwd63u8xt7" />
                {/* Ghost number — far right */}
                <div className="absolute right-8 top-1/2 -translate-y-1/2 font-bold select-none leading-none text-white" style={{
              fontSize: '8rem',
              opacity: 0.1
            }} aria-hidden="true" data-id="element-296" data-mp="3hjr1qs1b0zxo">
                  {part.num}
                </div>
                {/* Content — left side */}
                <div className="relative z-10 flex items-center gap-6 px-10 py-10" data-id="element-297" data-mp="spzi3tpq2r3u">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full border-2 border-gold/60 flex items-center justify-center" data-id="element-298" data-mp="nqd1rbelkuqt">
                    <span className="text-gold-light font-bold text-sm tracking-widest" data-id="element-299" data-mp="jnq7l3ugexqg">
                      {part.num}
                    </span>
                  </div>
                  <div data-id="element-300" data-mp="9qbt39s3cj0s">
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-1 leading-tight" data-id="element-301" data-mp="9rn7e3j5x965">
                      {part.title}
                    </h3>
                    <p className="text-white/70 text-base md:text-lg leading-relaxed" data-id="element-302" data-mp="4y28m99qwb5w">
                      {part.desc}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── (light bg) */}
      <section className="py-24 px-4 bg-sand-lightest overflow-hidden" data-id="element-303" data-mp="3i5vifxpn5hak">
        <div className="max-w-6xl mx-auto" data-id="element-304" data-mp="3i8ribrl1ug1n">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-305" data-mp="1xnb8ifba1g24">
            <p className="font-accent italic text-gold-dark mb-4 text-xl" data-id="element-306" data-mp="22n7bcb88n1g3">
              Tikros transformacijos istorijos
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-307" data-mp="27nve1u5tx6q5" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-308" data-mp="1jwpv364ldybm">
              Dalyvių Atsiliepimai
            </h2>
          </motion.div>

          {(() => {
          const testimonials = [{
            name: 'Eglė V.',
            role: 'Kursų dalyvė',
            quote: 'Nuo pirmos dienos jaučiau, kad patekau į saugią ir gerai apgalvotą mokymosi struktūrą, bet kartu apsupta labai daug šilumos ir palaikymo. DFB padėjo man atpažinti ir naviguoti neramius jausmus ir chaotiškas būsenas, bei juos keisti. Sugrįžti į čia ir dabar. Prisiimti atsakomybę už savo dėmesį. Nuosekliai kartojant pratimus ir darant praktikas įvyko pozityvus pokytis.'
          }, {
            name: 'Ieva M.',
            role: 'Kursų dalyvė',
            quote: 'Labiausiai patiko, kad 99% mokymų buvo patyrimas. Suvokimas visko per save. Būtent todėl DFB lengvai integravosi į mano gyvenimą. Perskaičiau ne vieną aprašymą apie DFB internete - visiškai neatspindi to, ką gauni. O gauni be galo daug. Sakyčiau, kad DFB - didelis asmeninis virsmas.'
          }, {
            name: 'Mantas R.',
            role: 'Kursų dalyvis',
            quote: 'Pagrindiniai įsisamoninimai ir suvokimai atėję nuoširdžiai praktikuojant ir naudojant DFB metodą yra: išmokimas, kaip keisti savo būsenas ir stebėti kaip per tai keičiasi ir aplinka, žmonės bei jų reakcijos; suvokimas, kaip įgalinti savo naudai visas būsenas neskirstant jų į geras ir blogas.'
          }, {
            name: 'Dovydas S.',
            role: 'Kursų dalyvis',
            quote: 'Labai aiškiai ir struktūruotai perteikiama kurso informacija, palydima per patyrimus. Isiklausymas į tai, kas kiekvienam aktualu, koks kiekvieno mokinio iššūkis, ir atsižvelgimas į tai. Rūpestis erdve, atmosfera, lankstumas situacijoms. Nuolatinis palaikymas, domėjimasis, įkvėpimas per konkrečius pavyzdžius. Dėkui už transformaciją ir nepakartojama pokytį.'
          }];
          const goToTestimonial = (idx: number) => {
            setTestimonialDirection(idx > activeTestimonial ? 1 : -1);
            setActiveTestimonial(idx);
          };
          const prevTestimonial = () => goToTestimonial(activeTestimonial === 0 ? testimonials.length - 1 : activeTestimonial - 1);
          const nextTestimonial = () => goToTestimonial(activeTestimonial === testimonials.length - 1 ? 0 : activeTestimonial + 1);
          const currentTestimonial = testimonials[activeTestimonial];
          const slideVariants = {
            enter: (dir: number) => ({
              opacity: 0,
              x: dir * 60
            }),
            center: {
              opacity: 1,
              x: 0
            },
            exit: (dir: number) => ({
              opacity: 0,
              x: dir * -60
            })
          };
          return <div className="relative flex flex-col items-center" data-id="element-309" data-mp="2vcx0h8y384mc">
                <div className="flex items-center w-full gap-4 md:gap-8" data-id="element-310" data-mp="3sriz1gwvjh92">
                  {/* Left arrow */}
                  <button onClick={prevTestimonial} aria-label="Ankstesnis" className="flex-shrink-0 w-12 h-12 rounded-full border-2 border-gold/40 bg-white hover:bg-gold hover:border-gold text-gold-dark hover:text-white flex items-center justify-center transition-all duration-200 shadow-sm" data-id="element-311" data-mp="3o12ktul8brln">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-312" data-mp="96tr9wpouzmh">
                      <polyline points="15 18 9 12 15 6" data-id="element-313" data-mp="rwjloipjhjdv" />
                    </svg>
                  </button>

                  {/* Testimonial content */}
                  <div className="flex-1 min-w-0 overflow-hidden" data-id="element-314" data-mp="11tobcsilfwzf">
                    <AnimatePresence mode="wait" custom={testimonialDirection} data-id="element-315" data-mp="11rbktqyrsear">
                      <motion.div key={activeTestimonial} custom={testimonialDirection} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
                    duration: 0.45,
                    ease: 'easeInOut'
                  }} className="flex flex-col items-center text-center px-2 md:px-8" data-id="element-316" data-mp="16t0wn8ttgvzw">
                        {/* Decorative quote mark */}
                        <span className="font-serif text-gold/25 select-none leading-none mb-2" style={{
                      fontSize: '6rem',
                      lineHeight: 0.8
                    }} aria-hidden="true" data-id="element-317" data-mp="14bsfzwzdgzbz">
                          &ldquo;
                        </span>

                        {/* Quote text — dynamically from current testimonial */}
                        <p className="text-mountain-darker text-lg md:text-xl leading-relaxed whitespace-pre-line max-w-2xl mx-auto mb-10 font-[400]" data-id="element-318" data-mp="2wb4xngy3jc5c">
                          {currentTestimonial.quote}
                        </p>

                        {/* Gold divider */}
                        <div className="w-12 h-px bg-gold mb-4" data-id="element-319" data-mp="2sc5wm7fhjzj6" />

                        {/* Name & role */}
                        <p className="font-bold text-mountain-darker text-lg" data-id="element-320" data-mp="3g207ijg8ve0s">
                          {currentTestimonial.name}
                        </p>
                        <p className="text-sm text-mountain-dark/60 mt-0.5" data-id="element-321" data-mp="363u8c9ltolzm">
                          {currentTestimonial.role}
                        </p>
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  {/* Right arrow */}
                  <button onClick={nextTestimonial} aria-label="Kitas" className="flex-shrink-0 w-12 h-12 rounded-full border-2 border-gold/40 bg-white hover:bg-gold hover:border-gold text-gold-dark hover:text-white flex items-center justify-center transition-all duration-200 shadow-sm" data-id="element-322" data-mp="1zni7u1vj2s0v">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-323" data-mp="2i060xlhpe5ef">
                      <polyline points="9 18 15 12 9 6" data-id="element-324" data-mp="1zar1twlkvqco" />
                    </svg>
                  </button>
                </div>

                {/* Dot indicators */}
                <div className="flex items-center gap-3 mt-10" data-id="element-325" data-mp="16nn1w61ce27r">
                  {testimonials.map((_, i) => <button key={i} onClick={() => goToTestimonial(i)} aria-label={`Atsiliepimas ${i + 1}`} className="transition-all duration-300 rounded-full" style={{
                width: i === activeTestimonial ? '2rem' : '0.5rem',
                height: '0.5rem',
                background: i === activeTestimonial ? 'rgb(var(--gold))' : 'rgba(var(--gold), 0.3)'
              }} data-id="element-326" data-mp="1bkwprh9325th" data-mpi={i} />)}
                </div>
              </div>;
        })()}
        </div>
      </section>

      {/* ── TEAM ── (dark bg) */}
      <section id="komanda" className="py-32 px-4 bg-mountain-darker text-white" data-id="element-327" data-mp="2s6qg0ji4zxc6">
        <div className="max-w-4xl mx-auto" data-id="element-328" data-mp="2n5sz0a8v59c9">
          <motion.div className="text-center mb-20" {...fadeInUp} data-id="element-329" data-mp="2n8ymjl1ug35v">
            <p className="font-accent italic text-gold-light mb-4 text-xl" data-id="element-330" data-mp="2i92i5kp8i9ps">
              Patyrę instruktoriai ir praktikai
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
          }} data-id="element-331" data-mp="28mi47naqzhgh" />
            <h2 className="text-4xl md:text-5xl font-bold text-white" data-id="element-332" data-mp="23lkn7e1h4tgk">
              Mūsų Komanda
            </h2>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-20" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-333" data-mp="2w9q94449kohz">
            <motion.div variants={fadeInUp} className="flex flex-col items-center text-center" data-id="element-334" data-mp="3a9r8ff4vrnvc">
              <div className="relative w-52 h-52 mx-auto mb-8 rounded-full overflow-hidden ring-2 ring-gold ring-offset-8 ring-offset-mountain-darker flex-shrink-0" data-id="element-335" data-mp="3f10wibas6j54">
                <div className="absolute inset-0 bg-cover" style={{
                backgroundImage: "url('https://cdn.magicpatterns.com/uploads/p5KUhhvxDrMn2D53eKDHKt/agne3.jpg')",
                backgroundPosition: 'center 30%'
              }} data-id="element-336" data-mp="ymp4u4kwi50o" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-1" data-id="element-337" data-mp="kmmoims8rjnj">
                Agnė Kubertė
              </h3>
              <p className="text-sm uppercase tracking-widest text-gold-light font-semibold mb-6" data-id="element-338" data-mp="fpo08kv9e6x9">
                DFB metodologijos Instruktorė
              </p>
              <div className="w-12 h-px bg-gold/40 mb-6" data-id="element-339" data-mp="pko6f4dxlcxf" />
              <p className="text-white/70 leading-relaxed mb-8 max-w-xs" data-id="element-340" data-mp="25zh811wvp1vy">
                DFB metodikos sertifikuota instruktorė, daugiau nei 10 metų
                meditacijų praktikė. Mokymuose ji padeda žmonėms sujungti
                meditaciją su kasdieniu gyvenimu – įnešti daugiau aiškumo,
                sąmoningo buvimo ir vidinės ramybės. Padeda dalyviams išmokti
                savireguliacijos ir lengvai pritaikyti meditacijos principus
                realiose gyvenimo situacijose.
              </p>
              <div className="relative max-w-xs" data-id="element-341" data-mp="ojk0v67qii5e">
                <span className="absolute -top-4 -left-2 font-serif text-gold/30 select-none leading-none" style={{
                fontSize: '4rem',
                lineHeight: 1
              }} aria-hidden="true" data-id="element-342" data-mp="ogxkqi6s2ubk">
                  "
                </span>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-col items-center text-center" data-id="element-343" data-mp="179096fpnvewd">
              <div className="relative w-52 h-52 mx-auto mb-8 rounded-full overflow-hidden ring-2 ring-gold ring-offset-8 ring-offset-mountain-darker flex-shrink-0" data-id="element-344" data-mp="1h7pmdv6m4b6a">
                <div className="absolute inset-0 bg-cover" style={{
                backgroundImage: "url('https://cdn.magicpatterns.com/uploads/cYk35xR7d8THHiA5ofpAZk/Screenshot_2026-03-06_at_13.56.34.png')",
                backgroundPosition: 'center 40%'
              }} data-id="element-345" data-mp="1r9td9rfx2hnj" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-1" data-id="element-346" data-mp="2a1m69luzk2v9">
                Vestina Zablockytė
              </h3>
              <p className="text-sm uppercase tracking-widest text-gold-light font-semibold mb-6" data-id="element-347" data-mp="255nc1hmz7qmx">
                DFB metodologijos Instruktorė
              </p>
              <div className="w-12 h-px bg-gold/40 mb-6" data-id="element-348" data-mp="1wa7n9f12w1o7" />
              <p className="text-white/70 leading-relaxed mb-8 max-w-xs" data-id="element-349" data-mp="2ssn4u8s71wea">
                „Sąmoningas judėjimas, šokis, rebefingas, Vipasana... Per 15
                metų leidausi į daugybę savęs pažinimo kelionių ir išbandžiau
                pačias įvairiausias praktikas. Tačiau būtent DFB metodika, kuria
                aktyviai gyvenu pastaruosius šešerius metus, tapo ta praktika,
                kuri iš esmės pakeitė mano kasdienybės kokybę. Šiandien noriu
                pasidalinti atradimu, kad medituoti lengva! Vietoj ilgų paieškų
                kviečiu iškart nerti į procesą, kuris atveria neįtikėtinai platų
                galimybių spektrą. Prisijunk!“
              </p>
              <div className="relative max-w-xs" data-id="element-350" data-mp="8cyfif9dkle0">
                <span className="absolute -top-4 -left-2 font-serif text-gold/30 select-none leading-none" style={{
                fontSize: '4rem',
                lineHeight: 1
              }} aria-hidden="true" data-id="element-351" data-mp="8et2s084cpl9">
                  "
                </span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── SCHEDULE ── (light bg) */}
      <section id="tvarkarastis" className="pt-24 pb-12 px-4 bg-sand-lightest" data-id="element-352" data-mp="3dc5irdywm590">
        <div className="max-w-6xl mx-auto" data-id="element-353" data-mp="38cj5j4j2sp07">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-354" data-mp="38dlsx2h6w9bd">
            <p className="font-accent italic text-gold-dark mb-4 text-xl" data-id="element-355" data-mp="3n170p9p0ewpm">
              Prisijunk prie rudens grupės
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-356" data-mp="3i8vwvvr5eqrs" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-357" data-mp="9un9d23hgq6h">
              Artėjantis Kursas
            </h2>
          </motion.div>

          <CourseCalendar course={currentCourse} data-id="element-358" data-mp="4thi78ph1az2" />

          <motion.div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12" {...fadeInUp} data-id="element-359" data-mp="2bmtcu4u9ah9l">
            <motion.button onClick={onViewCourses} whileHover={{
            scale: 1.03
          }} whileTap={{
            scale: 0.97
          }} transition={{
            duration: 0.15,
            ease: [0.23, 1, 0.32, 1]
          }} className="inline-flex items-center gap-2 border border-mountain-darker/25 text-mountain-darker px-9 py-[13px] rounded-full text-base font-semibold bg-white" data-id="element-360" data-mp="26p05v9l4pj89">
              Kiti kursai
              <ArrowRightIcon className="w-4 h-4" data-id="element-361" data-mp="16urxggs0y6v9" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* ── KONTAKTAI (4 variantų peržiūra) ── */}
      <ContactSection data-id="element-362" data-mp="1gsftda1d9kf0" />

      {/* ── FOOTER ── */}
      <footer className="py-12 px-4 bg-sand-lightest border-t border-sand-dark/20" data-id="element-363" data-mp="1gt7lubzo3212">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-364" data-mp="2n7goxemhc0l2">
          <div className="flex items-center gap-2" data-id="element-365" data-mp="2s58hmauy7qqb">
            <Logo className="h-8" data-id="element-366" data-mp="gk7ws59vrtds" />
          </div>
          <p className="text-mountain-dark/50 text-sm" data-id="element-367" data-mp="giwte6kcknn7">
            © 2026 tavo būsena. Visos teisės saugomos.
          </p>
          <div className="flex items-center gap-6" data-id="element-368" data-mp="6twv6pdo5wa2">
            <a href="#apie" className="text-mountain-dark/50 hover:text-mountain-darker text-sm transition-colors" data-id="element-369" data-mp="6slpgk0nyegb">
              Apie
            </a>
            <a href="#metodika" className="text-mountain-dark/50 hover:text-mountain-darker text-sm transition-colors" data-id="element-370" data-mp="3t1wfdqtyvm9o">
              Metodika
            </a>
            <a href="#registracija" className="text-mountain-dark/50 hover:text-mountain-darker text-sm transition-colors" data-id="element-371" data-mp="3k180yv0nvjw9">
              Registracija
            </a>
          </div>
        </div>
      </footer>
    </main>;
}